/**
 * The arm this browser is in for the TM+ paywall copy test.
 *
 * Assignment happens in <head>, before the bundle loads — lume.php opts the page
 * in via $activeExperiments and views/partials/head.php renders
 * ExperimentService's script, which picks a variant, persists it to localStorage
 * and a cookie, and hangs it on window.ExperimentsService.variants. So by the
 * time anything here runs the arm is already decided, and the first banner a
 * student meets is in it.
 *
 * BROWSER-SCOPED, NOT USER-SCOPED. The same student on a phone and a laptop can
 * be in two different arms. That is ExperimentService's design and it is
 * accepted here; moving the assignment onto accounts_user is the only fix and it
 * is not worth it for a copy test.
 */

import { ga4Event } from "../../main/utils/analytics";

/**
 * The id in ExperimentService::create() and in GA4's experiment_id.
 *
 * One arm per student for every paywall AND the conversion modal's title, so
 * every banner, the modal and the chat's unlock card report under this one id:
 * a GA4 event carries a single experiment_id, and a second test on any of them
 * would leave one of the two without data.
 */
export const PAYWALL_EXPERIMENT_ID = "lume_paywall_retafinal_v1";

/**
 * The user-scoped counterpart, for the cuts an event parameter cannot give:
 * "converted at all, having been in arm b". The event dimensions stay where they
 * are (lib/tracking) and remain the primary read — see trackPaywallExperiment.
 *
 * GA4 caps a user property name at 24 characters. The version marker stays on
 * purpose: archiving the dimension frees the slot, and a later test reusing this
 * name would splice two experiments into one series.
 *
 * ONE CAVEAT THE EVENT DIMENSIONS DO NOT HAVE. A user property is last-write-
 * wins on the GA4 user record, and the assignment underneath it is per browser
 * (see above). So a logged-in student who meets a banner on a laptop in arm `a`
 * overwrites the `b` their phone reported. The per-event `experiment_variant`
 * keeps the arm each impression really belonged to, which is why it stays the
 * primary cut and this is the secondary one.
 */
export const PAYWALL_USER_PROPERTY = "exp_lume_retafinal_v1";

/** a = the winning copy of the previous test, b = the new copy. */
export type PaywallVariant = "a" | "b";

const VARIANTS: readonly PaywallVariant[] = ["a", "b"];

const FALLBACK: PaywallVariant = "a";

interface ExperimentsService {
  variants?: Record<string, string | undefined>;
  /** Truthy only on the load where experiment_view actually went out. */
  trackFeatureFlag?: (id: string) => boolean | void;
}

function service(): ExperimentsService | null {
  const found = (window as unknown as Record<string, unknown>)
    .ExperimentsService;

  return typeof found === "object" && found !== null
    ? (found as ExperimentsService)
    : null;
}

/**
 * Whether the arm came from the QA override rather than from the split.
 *
 * The key is read out of EXPERIMENT_CONFIG instead of being written here, so
 * `rf` lives in exactly one place — ExperimentService::create(). It matters only
 * for the user property: an override sticks to the GA4 user record and would
 * park whoever was testing in an arm they were never assigned to.
 */
function variantIsForced(id: string, variants: readonly string[]): boolean {
  const config = (window as unknown as Record<string, unknown>)
    .EXPERIMENT_CONFIG as
    | Record<string, { key?: string } | undefined>
    | undefined;

  const key = config?.[id]?.key;

  if (!key) return false;

  const forced = new URLSearchParams(window.location.search).get(key);

  return variants.some((variant) => variant === forced);
}

/**
 * Which copy to render.
 *
 * Falls back to `a` — the control — whenever the assignment is
 * missing rather than picking one here. It is missing for real reasons: a
 * crawler gets no script at all (ExperimentService::isCrawler), and a blocked
 * localStorage means the value never persisted. In both cases the control arm is
 * the right thing to show, and a second assignment in the bundle would disagree
 * with the one GA4 was told about.
 */
export function paywallVariant(): PaywallVariant {
  return assignedVariant(PAYWALL_EXPERIMENT_ID, VARIANTS, FALLBACK);
}

function assignedVariant<T extends string>(
  id: string,
  variants: readonly T[],
  fallback: T
): T {
  const assigned = service()?.variants?.[id];

  return variants.find((variant) => variant === assigned) ?? fallback;
}

/**
 * Announces that the student has actually SEEN a banner in this test.
 *
 * `feature_flag_variable` experiments do not fire experiment_view on their own —
 * pushGtmEvents skips them on purpose — because assignment happens on every
 * Lume page load and only a fraction of those loads ever show a paywall.
 * Reporting a view on all of them would put students who never met a banner in
 * the denominator and halve every arm's rate.
 *
 * Fires at most once per page load (ExperimentsService keeps that flag), so
 * calling it from every banner's view is safe and is what lib/tracking does.
 *
 * The user property rides on the same call for the same reason, and only when
 * trackFeatureFlag reports that the event went out: that one return value
 * already accounts for the three cases where nothing should be reported — the
 * experiment suppressed on this page, this load already counted, and no
 * assignment at all (a crawler, or a browser that refused to persist it). The
 * last one is why paywallVariant() cannot be the source here: it answers with
 * the control arm when the assignment is missing, which is right for rendering
 * and would be a phantom `a` in the data.
 */
export function trackPaywallExperiment(): void {
  reportExposure(PAYWALL_EXPERIMENT_ID, PAYWALL_USER_PROPERTY, VARIANTS, paywallVariant);
}

function reportExposure(
  id: string,
  userProperty: string,
  variants: readonly string[],
  current: () => string
): void {
  if (!service()?.trackFeatureFlag?.(id)) return;

  ga4Event({ [userProperty]: variantIsForced(id, variants) ? "qa" : current() });
}

/**
 * The second test: where Lume opens. A browser arriving at exactly `/lume/` lands
 * on Início (`a`, control) or on the Tutor (`b`). The decision and the redirect
 * live in the Início route guard (lib/onboarding-guard), not here.
 */
export const LANDING_EXPERIMENT_ID = "lume_landing_tutor_v1";

/** The landing test's user-scoped counterpart. Same 24-character cap applies. */
export const LANDING_USER_PROPERTY = "exp_lume_landing_tutor";

/** a = Início, b = o Tutor. */
export type LandingVariant = "a" | "b";

const LANDING_VARIANTS: readonly LandingVariant[] = ["a", "b"];

/**
 * Which screen `/lume/` opens on. Falls back to `a` for the same reasons as
 * paywallVariant: no assignment means the control, never a second pick here.
 */
export function landingVariant(): LandingVariant {
  return assignedVariant(LANDING_EXPERIMENT_ID, LANDING_VARIANTS, "a");
}

/**
 * Announces exposure to the landing test. Called by the Início guard on the one
 * evaluation per session that decides the landing, in BOTH arms and before any
 * redirect — the control arm's exposure is the Início it was about to render.
 */
export function trackLandingExperiment(): void {
  reportExposure(LANDING_EXPERIMENT_ID, LANDING_USER_PROPERTY, LANDING_VARIANTS, landingVariant);
}
